/**
 * Help & Contact drawer — a docked side panel (same chrome as Notes /
 * TradeJournal) listing how to reach the owner for support, access, or
 * billing questions.
 *
 * Contact details live in the CONTACT constant below so they're trivial
 * to update in one place. Channels with an empty `value` are skipped, so
 * filling in a Telegram / WhatsApp handle is a one-line change.
 */
import { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "@/state/hooks";
import { setHelpOpen } from "@/state/slices/uiSlice";
import { updateAnalyticsPrefs } from "@/state/slices/meSlice";
import styles from "./HelpPanel.module.css";

// ── Owner contact info ──────────────────────────────────────────────
// Email is wired for real; the others are placeholders — drop the real
// handle/number/URL in and they light up automatically.
const CONTACT: {
  name: string;
  blurb: string;
  email: string;
  telegram: string;
  whatsapp: string;
  website: string;
} = {
  name: "Hoang Nguyen",
  blurb: "Questions about access, your account, or billing? Reach out directly — happy to help.",
  email: "hoangnguyenhong1993@gmail.com",
  // TODO(owner): fill these in with your real handles.
  telegram: "", // e.g. "@hoangnh" — rendered as https://t.me/hoangnh
  whatsapp: "", // e.g. "+84 912 345 678"
  website: "pulsarix.net",
};

const MailIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden>
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <path d="M3.5 6.5 12 13l8.5-6.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const TelegramIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden>
    <path d="M21 4 3 11l5 2 2 6 3-4 5 4 3-15z" strokeLinejoin="round" />
    <path d="M8 13l9-6-6 8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const WhatsAppIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden>
    <path d="M4 20l1.4-4A8 8 0 1 1 9 19.5z" strokeLinejoin="round" />
    <path d="M9 9c0 4 2 6 6 6 1.5 0 1.5-2 0-2-1 0-3-2-3-3 0-1.5-2-1.5-2 0z" strokeLinejoin="round" />
  </svg>
);
const WebIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden>
    <circle cx="12" cy="12" r="9" />
    <path d="M3 12h18M12 3c2.5 2.5 2.5 15 0 18M12 3c-2.5 2.5-2.5 15 0 18" />
  </svg>
);
const HelpGlyph = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
    <circle cx="12" cy="12" r="9" />
    <path d="M9.2 9.3a2.8 2.8 0 0 1 5.3 1c0 1.8-2.5 2.2-2.5 3.7" strokeLinecap="round" />
    <circle cx="12" cy="17.2" r="0.9" fill="currentColor" stroke="none" />
  </svg>
);

type Row = {
  Icon: () => JSX.Element;
  label: string;
  value: string;
  href: string;
};

function buildRows(): Row[] {
  const rows: Row[] = [];
  if (CONTACT.email) {
    rows.push({
      Icon: MailIcon,
      label: "Email",
      value: CONTACT.email,
      href: `mailto:${CONTACT.email}`,
    });
  }
  if (CONTACT.telegram) {
    const handle = CONTACT.telegram.replace(/^@/, "");
    rows.push({
      Icon: TelegramIcon,
      label: "Telegram",
      value: CONTACT.telegram.startsWith("@") ? CONTACT.telegram : `@${handle}`,
      href: `https://t.me/${handle}`,
    });
  }
  if (CONTACT.whatsapp) {
    const digits = CONTACT.whatsapp.replace(/[^\d]/g, "");
    rows.push({
      Icon: WhatsAppIcon,
      label: "WhatsApp",
      value: CONTACT.whatsapp,
      href: `https://wa.me/${digits}`,
    });
  }
  if (CONTACT.website) {
    const url = CONTACT.website.startsWith("http")
      ? CONTACT.website
      : `https://${CONTACT.website}`;
    rows.push({
      Icon: WebIcon,
      label: "Website",
      value: CONTACT.website.replace(/^https?:\/\//, ""),
      href: url,
    });
  }
  return rows;
}

export function HelpPanel() {
  const dispatch = useAppDispatch();
  const open = useAppSelector((s) => s.ui.helpOpen);
  const shareUsage = useAppSelector((s) => !s.me.analyticsOptOut);

  // Close on Escape, mirroring the Notes / Journal drawers.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") dispatch(setHelpOpen(false));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, dispatch]);

  if (!open) return null;

  const rows = buildRows();
  const initials = CONTACT.name
    .split(/\s+/)
    .map((p) => p[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <aside className={styles.drawer} aria-label="Help and contact">
      <div className={styles.header}>
        <div className={styles.title}>
          <span className={styles.titleIco}>
            <HelpGlyph />
          </span>
          Help &amp; Contact
        </div>
        <button
          type="button"
          className={styles.iconBtn}
          title="Close"
          aria-label="Close help panel"
          onClick={() => dispatch(setHelpOpen(false))}
        >
          ×
        </button>
      </div>

      <div className={styles.body}>
        <div className={styles.card}>
          <div className={styles.avatar}>{initials}</div>
          <div className={styles.who}>
            <div className={styles.name}>{CONTACT.name}</div>
            <div className={styles.roleTag}>Support</div>
          </div>
        </div>

        <p className={styles.blurb}>{CONTACT.blurb}</p>

        <div className={styles.rows}>
          {rows.map((r) => (
            <a
              key={r.label}
              className={styles.row}
              href={r.href}
              target={r.label === "Email" ? undefined : "_blank"}
              rel="noopener noreferrer"
            >
              <span className={styles.rowIco}>
                <r.Icon />
              </span>
              <span className={styles.rowMeta}>
                <span className={styles.rowLabel}>{r.label}</span>
                <span className={styles.rowValue}>{r.value}</span>
              </span>
              <span className={styles.rowChevron} aria-hidden>
                ›
              </span>
            </a>
          ))}
        </div>

        <div className={styles.foot}>
          Typical reply within a day. Please include your account email so we
          can find you quickly.
        </div>
        <label className={styles.usage}>
          <span className={styles.usageMeta}>
            <span className={styles.usageLabel}>Share usage data</span>
            <span className={styles.usageHint}>
              Helps us see which features and strategies are most useful.
              Stays on our servers.
            </span>
          </span>
          <input
            type="checkbox"
            className={styles.switch}
            checked={shareUsage}
            onChange={(e) =>
              dispatch(
                updateAnalyticsPrefs({ opt_out: !e.target.checked, notice_seen: true }),
              )
            }
          />
        </label>
        <div className={styles.legal}>
          <a href="/privacy" target="_blank" rel="noopener noreferrer">
            Privacy Policy
          </a>
          <span aria-hidden>·</span>
          <a href="/terms" target="_blank" rel="noopener noreferrer">
            Terms of Service
          </a>
        </div>
      </div>
    </aside>
  );
}
